import { useEffect, useRef, useState, useCallback } from 'react';
import type { ClipDataPoint } from '../lib/api-clip-monitor';

const LOG = (msg: string, ...args: unknown[]) => {
  const serialized = args.map(a => (typeof a === 'object' && a !== null) ? JSON.stringify(a) : a);
  console.log(`[DiaperSelection] ${msg}`, ...serialized);
};

export type DiaperLinkStatus =
  | null
  | 'searching'
  | 'found'
  | { error: string; details?: string };

type SelectionState =
  | { kind: 'idle' }
  | { kind: 'pending_counter'; counter: number }
  | { kind: 'pending_start'; diaperStart: string; fullLoadTriggered: boolean }
  | { kind: 'resolved' }
  | { kind: 'failed' };

interface UseDiaperSelectionParams {
  clipAddress: string | null;
  urlClipParam: string | null;
  urlDiaperParam: string | null;
  urlDiaperStartParam: string | null;
  availableDiaperCounters: number[];
  logicalDiaperGroups: Record<number, ClipDataPoint[]>;
  selectedDiaperCounter: number | null;
  clipDataLoadLimit: number | null;
  loadingData: boolean;
  clipDataLength: number;
  loadClipDataFull: () => void;
  setSelectedDiaperCounter: (c: number | null) => void;
  addToast: (message: string, type: 'info' | 'success' | 'warning' | 'error') => void;
}

interface UseDiaperSelectionResult {
  linkStatus: DiaperLinkStatus;
  dismissLinkError: () => void;
}

const MATCH_WINDOW_MS = 5 * 60 * 1000;

export function useDiaperSelection(params: UseDiaperSelectionParams): UseDiaperSelectionResult {
  const {
    clipAddress,
    urlClipParam,
    urlDiaperParam,
    urlDiaperStartParam,
    availableDiaperCounters,
    logicalDiaperGroups,
    selectedDiaperCounter,
    clipDataLoadLimit,
    loadingData,
    clipDataLength,
    loadClipDataFull,
    setSelectedDiaperCounter,
    addToast,
  } = params;

  const [linkStatus, setLinkStatus] = useState<DiaperLinkStatus>(null);

  // Internal state machine ref — survives across renders without causing re-renders.
  // Reset when clipAddress changes (new navigation).
  const stateRef = useRef<SelectionState>({ kind: 'idle' });
  const prevClipRef = useRef<string | null>(null);
  const prevHighestCounterRef = useRef<number | null>(null);

  // On clip change: capture URL params into the state machine and reset.
  // Only use diaper URL params if the URL's clip param matches the new clip —
  // otherwise the user changed clips manually via the dropdown and stale
  // URL params from a previous navigation should be ignored.
  if (clipAddress !== prevClipRef.current) {
    prevClipRef.current = clipAddress;
    prevHighestCounterRef.current = null;
    setLinkStatus(null);

    const clipMatchesUrl = clipAddress !== null && urlClipParam === clipAddress;

    if (clipMatchesUrl && urlDiaperParam) {
      const counter = parseInt(urlDiaperParam, 10);
      stateRef.current = isNaN(counter)
        ? { kind: 'idle' }
        : { kind: 'pending_counter', counter };
      LOG('Clip changed: pending counter', { clipAddress, counter: urlDiaperParam });
    } else if (clipMatchesUrl && urlDiaperStartParam) {
      stateRef.current = { kind: 'pending_start', diaperStart: urlDiaperStartParam, fullLoadTriggered: false };
      LOG('Clip changed: pending diaperStart', { clipAddress, diaperStart: urlDiaperStartParam });
    } else {
      stateRef.current = { kind: 'idle' };
      LOG('Clip changed: idle', { clipAddress, clipMatchesUrl });
    }
  }

  // Single unified effect: handles URL param resolution AND auto-select-newest
  useEffect(() => {
    const state = stateRef.current;

    if (state.kind === 'resolved' || state.kind === 'failed') {
      return;
    }

    // === IDLE: no URL params — auto-select newest / detect new diapers ===
    if (state.kind === 'idle') {
      if (availableDiaperCounters.length === 0) return;

      const currentHighest = availableDiaperCounters[0];

      if (prevHighestCounterRef.current !== null &&
          currentHighest > prevHighestCounterRef.current &&
          selectedDiaperCounter !== currentHighest) {
        addToast(`🩲 New diaper detected! Counter: ${currentHighest}. Now monitoring the new diaper.`, 'success');
        setSelectedDiaperCounter(currentHighest);
      } else if (selectedDiaperCounter === null) {
        setSelectedDiaperCounter(currentHighest);
      }

      prevHighestCounterRef.current = currentHighest;
      return;
    }

    // === PENDING_COUNTER: looking for a specific firmware counter ===
    if (state.kind === 'pending_counter') {
      if (availableDiaperCounters.length === 0) {
        setLinkStatus('searching');
        return;
      }

      const { counter } = state;
      if (availableDiaperCounters.includes(counter)) {
        LOG('Counter found', counter);
        if (selectedDiaperCounter !== counter) {
          setSelectedDiaperCounter(counter);
        }
        stateRef.current = { kind: 'resolved' };
        setLinkStatus('found');
      } else if (!loadingData) {
        LOG('Counter NOT found', { counter, available: availableDiaperCounters });
        stateRef.current = { kind: 'failed' };
        setSelectedDiaperCounter(null);
        setLinkStatus({
          error: `Diaper #${counter} not found in clip data`,
          details: `Available diapers: ${availableDiaperCounters.map(c => '#' + c).join(', ')}`,
        });
      } else {
        setLinkStatus('searching');
      }
      return;
    }

    // === PENDING_START: looking for diaper by start time overlap ===
    if (state.kind === 'pending_start') {
      const groupCount = Object.keys(logicalDiaperGroups).length;
      if (availableDiaperCounters.length === 0 || groupCount === 0) {
        setLinkStatus('searching');
        return;
      }

      const targetMs = new Date(state.diaperStart).getTime();
      if (isNaN(targetMs)) {
        LOG('Invalid diaperStart timestamp');
        stateRef.current = { kind: 'failed' };
        setSelectedDiaperCounter(null);
        setLinkStatus({ error: 'Invalid diaper start time in URL' });
        return;
      }

      let bestCounter: number | null = null;
      let bestDistance = Infinity;
      for (const [counterStr, points] of Object.entries(logicalDiaperGroups)) {
        if (points.length === 0) continue;
        const groupStart = new Date(points[0].timestamp).getTime();
        const groupEnd = new Date(points[points.length - 1].timestamp).getTime();
        if (targetMs >= groupStart - MATCH_WINDOW_MS && targetMs <= groupEnd + MATCH_WINDOW_MS) {
          const dist = Math.abs(targetMs - groupStart);
          if (dist < bestDistance) {
            bestDistance = dist;
            bestCounter = Number(counterStr);
          }
        }
      }

      if (bestCounter !== null) {
        LOG('DiaperStart matched', { bestCounter, distance: bestDistance });
        if (selectedDiaperCounter !== bestCounter) {
          setSelectedDiaperCounter(bestCounter);
        }
        stateRef.current = { kind: 'resolved' };
        setLinkStatus('found');
      } else if (clipDataLoadLimit !== null && !state.fullLoadTriggered && !loadingData) {
        LOG('Not found in partial data, loading full clip');
        stateRef.current = { ...state, fullLoadTriggered: true };
        setLinkStatus('searching');
        loadClipDataFull();
      } else if (clipDataLoadLimit === null && clipDataLength > 0 && !loadingData) {
        const groupSummary = Object.entries(logicalDiaperGroups).map(([c, pts]) => {
          const s = pts.length > 0 ? new Date(pts[0].timestamp).toISOString() : '?';
          return `#${c} (start: ${s})`;
        }).join(', ');
        LOG('Not found even in full data — giving up', { diaperStart: state.diaperStart, groupSummary });
        stateRef.current = { kind: 'failed' };
        setSelectedDiaperCounter(null);
        setLinkStatus({
          error: `Diaper starting at ${new Date(state.diaperStart).toLocaleString()} not found`,
          details: `Searched ${groupCount} diapers in clip. Available: ${groupSummary}`,
        });
      } else {
        setLinkStatus('searching');
      }
      return;
    }
  }, [
    availableDiaperCounters,
    logicalDiaperGroups,
    selectedDiaperCounter,
    setSelectedDiaperCounter,
    clipDataLoadLimit,
    loadingData,
    clipDataLength,
    loadClipDataFull,
    addToast,
  ]);

  const dismissLinkError = useCallback(() => {
    setLinkStatus(null);
    stateRef.current = { kind: 'idle' };
  }, []);

  return { linkStatus, dismissLinkError };
}
